import moment from 'moment'
import isEqual from 'lodash/isEqual'

// Pure helpers behind `ModelListPage`: the list state (search text, page and
// one value per filter) lives in the URL so it survives navigating into a
// record and back, and can be shared as a link. This module converts that
// state to/from the query string and into the params `HttpHandler.list`
// sends to the backend's generic list API (`BaseModelListAPI`).
//
// Filter value shapes, by `type`:
// - `multi`       → { mode: 'include' | 'exclude', values: [...] }
// - `choice`      → one of the options' values
// - `boolean`     → true | false
// - `model`       → { values: [id, ...], labels: { [id]: label } }
// - `dateRange`   → { from: 'YYYY-MM-DD' | undefined, to: 'YYYY-MM-DD' | undefined }
// - `numberRange` → { min: number | undefined, max: number | undefined }
// A filter without a value (`undefined`) isn't applied.
//
// A definition's `toParams(value)` replaces the default request params for
// that filter, for filters that don't map 1:1 to a model field (e.g. a
// "status" that is really a combination of null checks).

export const FILTER_TYPE = {
  MULTI: 'multi',
  CHOICE: 'choice',
  BOOLEAN: 'boolean',
  MODEL: 'model',
  DATE_RANGE: 'dateRange',
  NUMBER_RANGE: 'numberRange',
}

export const SEARCH_PARAM = 'q'
export const PAGE_PARAM = 'pagina'
// Written when every filter was removed on a page whose defaults aren't
// empty, so reloading doesn't bring the defaults back.
export const NO_FILTERS_PARAM = 'filtros'
export const NO_FILTERS_VALUE = 'nenhum'

const RANGE_SEPARATOR = '..'
const EXCLUDE_PREFIX = '!'

const isSet = (v) => v !== undefined && v !== null && v !== ''

export const isFilterActive = (definition, value) => {
  if (value === undefined || value === null) return false

  switch (definition.type) {
    case FILTER_TYPE.MULTI:
    case FILTER_TYPE.MODEL:
      return !!value.values?.length
    case FILTER_TYPE.CHOICE:
      return isSet(value)
    case FILTER_TYPE.BOOLEAN:
      return typeof value === 'boolean'
    case FILTER_TYPE.DATE_RANGE:
      return !!value.from || !!value.to
    case FILTER_TYPE.NUMBER_RANGE:
      return isSet(value.min) || isSet(value.max)
    default:
      return false
  }
}

export const activeFilters = (definitions, filters = {}) =>
  definitions.reduce((acc, definition) => {
    const value = filters[definition.key]
    if (isFilterActive(definition, value)) acc[definition.key] = value
    return acc
  }, {})

const encodeFilter = (definition, value) => {
  switch (definition.type) {
    case FILTER_TYPE.MULTI:
      return `${
        value.mode === 'exclude' ? EXCLUDE_PREFIX : ''
      }${value.values.join(',')}`
    case FILTER_TYPE.MODEL:
      return value.values.join(',')
    case FILTER_TYPE.CHOICE:
    case FILTER_TYPE.BOOLEAN:
      return String(value)
    case FILTER_TYPE.DATE_RANGE:
      return `${value.from || ''}${RANGE_SEPARATOR}${value.to || ''}`
    case FILTER_TYPE.NUMBER_RANGE:
      return `${isSet(value.min) ? value.min : ''}${RANGE_SEPARATOR}${
        isSet(value.max) ? value.max : ''
      }`
    default:
      return undefined
  }
}

const parseNumber = (raw) => {
  if (!isSet(raw)) return undefined
  const number = Number(raw)
  return Number.isFinite(number) ? number : undefined
}

const decodeFilter = (definition, raw) => {
  if (raw === null || raw === '') return undefined

  switch (definition.type) {
    case FILTER_TYPE.MULTI: {
      const exclude = raw.startsWith(EXCLUDE_PREFIX)
      const values = (exclude ? raw.slice(EXCLUDE_PREFIX.length) : raw)
        .split(',')
        .filter(Boolean)
      return values.length
        ? { mode: exclude ? 'exclude' : 'include', values }
        : undefined
    }
    case FILTER_TYPE.MODEL: {
      // Labels are resolved separately (see `useModelList`).
      const values = raw.split(',').filter(Boolean)
      return values.length ? { values, labels: {} } : undefined
    }
    case FILTER_TYPE.CHOICE:
      return definition.options?.some((option) => option.value === raw)
        ? raw
        : undefined
    case FILTER_TYPE.BOOLEAN:
      if (raw === 'true') return true
      if (raw === 'false') return false
      return undefined
    case FILTER_TYPE.DATE_RANGE: {
      const [from, to] = raw.split(RANGE_SEPARATOR)
      return from || to
        ? { from: from || undefined, to: to || undefined }
        : undefined
    }
    case FILTER_TYPE.NUMBER_RANGE: {
      const [min, max] = raw.split(RANGE_SEPARATOR).map(parseNumber)
      return isSet(min) || isSet(max) ? { min, max } : undefined
    }
    default:
      return undefined
  }
}

/**
 * Reads the list state from a query string. With none of the list's own
 * params present, the page's `defaultFilters` apply.
 */
export const parseListSearch = (search, definitions, defaultFilters = {}) => {
  const params = new URLSearchParams(search)
  const page = parseInt(params.get(PAGE_PARAM), 10)

  const hasFilterParams =
    params.get(NO_FILTERS_PARAM) === NO_FILTERS_VALUE ||
    definitions.some((definition) => params.has(definition.key))

  const filters = hasFilterParams
    ? definitions.reduce((acc, definition) => {
        const value = decodeFilter(definition, params.get(definition.key))
        if (value !== undefined) acc[definition.key] = value
        return acc
      }, {})
    : activeFilters(definitions, defaultFilters)

  return {
    text: params.get(SEARCH_PARAM) || '',
    page: Number.isFinite(page) && page > 0 ? page : 1,
    filters,
  }
}

/**
 * Writes the list state back into `currentSearch`, keeping any param the
 * list doesn't own. Filters equal to the defaults are left out, so the
 * page's plain URL keeps meaning "the default view".
 */
export const buildListSearch = (
  state,
  definitions,
  defaultFilters = {},
  currentSearch = ''
) => {
  const params = new URLSearchParams(currentSearch)
  ;[SEARCH_PARAM, PAGE_PARAM, NO_FILTERS_PARAM].forEach((key) =>
    params.delete(key)
  )
  definitions.forEach((definition) => params.delete(definition.key))

  if (state.text) params.set(SEARCH_PARAM, state.text)
  if (state.page > 1) params.set(PAGE_PARAM, String(state.page))

  const filters = activeFilters(definitions, state.filters)
  const defaults = activeFilters(definitions, defaultFilters)

  if (!isSameFilters(definitions, filters, defaults)) {
    definitions.forEach((definition) => {
      if (filters[definition.key] !== undefined) {
        params.set(
          definition.key,
          encodeFilter(definition, filters[definition.key])
        )
      }
    })

    if (!Object.keys(filters).length) {
      params.set(NO_FILTERS_PARAM, NO_FILTERS_VALUE)
    }
  }

  const search = params.toString()
  return search ? `?${search}` : ''
}

// Compares filters by what reaches the URL: model labels are display data,
// filled in after the URL is read, and ids may be numbers or strings.
const comparable = (definitions, filters) =>
  definitions.reduce((acc, definition) => {
    const value = filters[definition.key]
    if (value === undefined) return acc
    acc[definition.key] = encodeFilter(definition, value)
    return acc
  }, {})

export const isSameFilters = (definitions, a = {}, b = {}) =>
  isEqual(
    comparable(definitions, activeFilters(definitions, a)),
    comparable(definitions, activeFilters(definitions, b))
  )

const nextDay = (date) => moment(date).add(1, 'day').format('YYYY-MM-DD')

/** The request params a single active filter contributes. */
export const filterParams = (definition, value) => {
  if (!isFilterActive(definition, value)) return {}
  if (definition.toParams) return definition.toParams(value)

  const param = definition.param || definition.key

  switch (definition.type) {
    case FILTER_TYPE.MULTI:
      return { [value.mode === 'exclude' ? `!${param}` : param]: value.values }
    case FILTER_TYPE.MODEL:
      return {
        [param]: definition.multiple ? value.values : value.values[0],
      }
    case FILTER_TYPE.CHOICE:
    case FILTER_TYPE.BOOLEAN:
      return { [param]: value }
    case FILTER_TYPE.DATE_RANGE:
      return {
        ...(value.from && { [`${param}__gte`]: value.from }),
        // `__lt` the following day keeps the whole "to" day in range for
        // datetime fields too.
        ...(value.to && { [`${param}__lt`]: nextDay(value.to) }),
      }
    case FILTER_TYPE.NUMBER_RANGE:
      return {
        ...(isSet(value.min) && { [`${param}__gte`]: value.min }),
        ...(isSet(value.max) && { [`${param}__lte`]: value.max }),
      }
    default:
      return {}
  }
}

/**
 * The params `HttpHandler.list` sends to the backend for the given state.
 * Each filter is sent under its `param` (defaults to its `key`), unless it
 * defines its own `toParams`. `baseParams` are sent on every request.
 */
export const buildRequestParams = (
  state,
  definitions,
  { orderBy, pageSize, baseParams } = {}
) => {
  const params = {
    ...(baseParams || {}),
    page: state.page,
    paginate_by: pageSize,
    order_by: orderBy,
    text_filter: state.text || undefined,
  }

  definitions.forEach((definition) => {
    Object.assign(
      params,
      filterParams(definition, state.filters[definition.key])
    )
  })

  return params
}

const formatDate = (date) => moment(date).format('DD/MM/YY')

const listSummary = (labels) =>
  labels.length > 2 ? `${labels[0]} +${labels.length - 1}` : labels.join(', ')

const optionLabel = (definition, value) =>
  definition.options?.find((option) => option.value === value)?.label ?? value

/** Short, human summary of an active filter's value, shown on its chip. */
export const summarizeFilter = (definition, value) => {
  if (!isFilterActive(definition, value)) return ''

  switch (definition.type) {
    case FILTER_TYPE.MULTI: {
      const text = listSummary(
        value.values.map((v) => optionLabel(definition, v))
      )
      return value.mode === 'exclude' ? `exceto ${text}` : text
    }
    case FILTER_TYPE.MODEL:
      return listSummary(value.values.map((id) => value.labels?.[id] || '…'))
    case FILTER_TYPE.CHOICE:
      return optionLabel(definition, value)
    case FILTER_TYPE.BOOLEAN:
      return value
        ? definition.trueLabel || 'Sim'
        : definition.falseLabel || 'Não'
    case FILTER_TYPE.DATE_RANGE:
      if (value.from && value.to)
        return value.from === value.to
          ? formatDate(value.from)
          : `${formatDate(value.from)} → ${formatDate(value.to)}`
      if (value.from) return `desde ${formatDate(value.from)}`
      return `até ${formatDate(value.to)}`
    case FILTER_TYPE.NUMBER_RANGE: {
      const format = definition.format || String
      if (isSet(value.min) && isSet(value.max))
        return `${format(value.min)} – ${format(value.max)}`
      if (isSet(value.min)) return `≥ ${format(value.min)}`
      return `≤ ${format(value.max)}`
    }
    default:
      return ''
  }
}
